/**
 * Runtime Extraction Adapter — Point Cloud ASPRS Classification
 *
 * P3 — INIT-2026-Q3-SCENE-HIERARCHY (runtime extraction adapters).
 * Builds Project → Scan → Class nodes from ASPRS classification codes
 * present in the point cloud metadata.
 *
 * Hierarchy: Project (LoR3) → Scan (LoR4) → Classification Class (LoR5)
 *
 * Detect: layerKind in {"copc", "potree", "pointcloud"}.
 */

import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type {
  TreeNode,
  SceneHierarchyManifest,
  TreeNodeDomain,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type {
  DomainAdapter,
  DomainAdapterContext,
  AdapterResult,
} from "../DomainAdapter";

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function generateUlid(): string {
  let t = Date.now();
  const tsChars: string[] = [];
  for (let i = 0; i < 10; i++) {
    tsChars.unshift(CROCKFORD[t % 32]);
    t = Math.floor(t / 32);
  }
  const randBytes = new Uint8Array(10);
  crypto.getRandomValues(randBytes);
  let rand = "";
  for (let i = 0; i < 10; i++) {
    rand += CROCKFORD[randBytes[i] & 0x1f];
    rand += CROCKFORD[(randBytes[i] >> 5) & 0x07 | ((i < 9 ? randBytes[i + 1] & 0x03 : 0) << 3)];
  }
  return tsChars.join("") + rand.slice(0, 16);
}

function usnMeta(id: string, parentId: string | null) {
  return {
    objectId: id,
    aspects: {
      spatial: { parentId },
      functional: { parentId },
    },
    relationships: [] as const,
  };
}

/**
 * Standard ASPRS LAS 1.4 classification codes.
 * Source: ASPRS LAS Specification 1.4 R15, Table 9.
 */
const ASPRS_CLASSES: Record<number, string> = {
  0: "Created, Never Classified",
  1: "Unclassified",
  2: "Ground",
  3: "Low Vegetation",
  4: "Medium Vegetation",
  5: "High Vegetation",
  6: "Building",
  7: "Low Point (Noise)",
  8: "Reserved (Model Key-Point)",
  9: "Water",
  10: "Rail",
  11: "Road Surface",
  12: "Reserved (Overlap)",
  13: "Wire – Guard (Shield)",
  14: "Wire – Conductor (Phase)",
  15: "Transmission Tower",
  16: "Wire-Structure Connector",
  17: "Bridge Deck",
  18: "High Noise",
};

const PC_LAYER_KINDS = new Set(["copc", "potree", "pointcloud"]);

export const PointCloudClassAdapter: DomainAdapter = {
  domain: "survey" as TreeNodeDomain,
  version: "1.0.0",
  standard: "ASPRS-LAS:1.4",

  detect(item: SceneItem): boolean {
    type Extended = SceneItem & { extensions?: { geosoft?: { domain?: string } } };
    const ext = (item as Extended).extensions?.geosoft;
    if (ext?.domain === "survey") return true;
    return PC_LAYER_KINDS.has(item.layerKind);
  },

  async buildManifest(
    item: SceneItem,
    asset: { layerId: string; assetNodeId: string; gtwinId: string },
    context: DomainAdapterContext,
  ): Promise<AdapterResult> {
    const nodes: TreeNode[] = [];
    const projectId = generateUlid();

    const projectNode: TreeNode = {
      id: projectId,
      parentId: null,
      lor: 3,
      label: asset.assetNodeId || asset.layerId,
      kind: "asset",
      layerId: asset.layerId,
      featureId: null,
      domain: "survey" as TreeNodeDomain,
      standardRef: {
        standard: "ASPRS-LAS:1.4",
        classUri: "",
        nativeId: asset.assetNodeId,
        propertySets: [],
        linkedDocuments: [],
      },
      metadata: { runtimeExtracted: true, source: "pointcloud-classification" },
      hasChildren: true,
      sortKey: 0,
      ...usnMeta(projectId, null),
    };
    nodes.push(projectNode);

    const scanId = generateUlid();
    const scanNode: TreeNode = {
      id: scanId,
      parentId: projectId,
      lor: 4,
      label: "Scan",
      kind: "scan",
      layerId: asset.layerId,
      featureId: null,
      domain: "survey" as TreeNodeDomain,
      standardRef: null,
      metadata: { runtimeExtracted: true },
      hasChildren: true,
      sortKey: 0,
      ...usnMeta(scanId, projectId),
    };
    nodes.push(scanNode);

    let classificationCodes: number[] = [];

    if (context.fetchJson) {
      const href =
        item.endpoints["href"] ?? Object.values(item.endpoints)[0] ?? "";
      try {
        const raw = await context.fetchJson(`${href}/classification.json`);
        if (Array.isArray(raw)) {
          classificationCodes = (raw as number[]).filter(
            (c) => typeof c === "number",
          );
        }
      } catch {
        // Use default common classes
      }
    }

    if (classificationCodes.length === 0) {
      classificationCodes = [1, 2, 3, 4, 5, 6];
    }

    for (let i = 0; i < classificationCodes.length; i++) {
      const code = classificationCodes[i];
      const classId = generateUlid();
      const classLabel = ASPRS_CLASSES[code] ?? `Class ${code}`;

      nodes.push({
        id: classId,
        parentId: scanId,
        lor: 5,
        label: classLabel,
        kind: "group",
        layerId: asset.layerId,
        featureId: `class:${code}`,
        domain: "survey" as TreeNodeDomain,
        standardRef: {
          standard: "ASPRS-LAS:1.4",
          classUri: `asprs:class/${code}`,
          nativeId: `${code}`,
          propertySets: [],
          linkedDocuments: [],
        },
        metadata: {
          runtimeExtracted: true,
          asprsClassCode: code,
          asprsClassName: classLabel,
        },
        hasChildren: false,
        sortKey: i,
        ...usnMeta(classId, scanId),
      });
    }

    scanNode.hasChildren = classificationCodes.length > 0;

    const manifest: SceneHierarchyManifest = {
      schema: "geosoft.scene-hierarchy/1",
      gtwinId: asset.gtwinId,
      rootLayerId: asset.layerId,
      generatedAt: new Date().toISOString(),
      domain: "survey",
      standardName: "ASPRS-LAS",
      standardVersion: "1.4",
      totalNodes: nodes.length,
      nodeCountByLoR: {},
      nodes,
      paginated: false,
      dataSources: {
        [asset.layerId]: {
          layerId: asset.layerId,
          label: "Point Cloud",
          mediaKind: item.layerKind,
          primary: true,
        },
      },
    };

    return { ok: true, manifest };
  },
};
